import Grid from '@mui/material/Grid';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import unionBy from 'lodash/unionBy';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';

import compareRoles from '~/utils/compareRoles';
import { extractErrorMessage } from '~/utils/error/error';
import removeActPrefix from '~/utils/text/removeActPrefix';
import useMemoCompare from '~/hooks/useMemoCompare';
import { DEBOUNCE_TIME_500 } from '~/ui/constants/common';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';

import { IUserRole } from '~/types';
import { getChangeRoleSuccessMessage } from '../helper';
import { IChangeRoleFormValues } from '../types';

import styles from './ChangeRole.module.scss';

let changeRolePayload: IChangeRoleFormValues | null = null;

const ChangeRole = (): ReactElement => {
  const [changingRole, setChangingRole] = useState(false);

  const { setClinic, onGetMyClinic } = useStoreActions(actions => actions.clinic);
  const { setAvailableClients, setClientOptionsByTeam, setDefaultClient, setCurrentClientOption } =
    useStoreActions(actions => actions.client);
  const { onChangeRole } = useStoreActions(actions => actions.user);
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const current = useStoreState(state => state.user.current);

  const { isProgramAssistant, role, team, clinic } = useRole();

  const {
    control,
    watch,
    setValue: setFormValue,
    formState: { errors },
  } = useForm<IChangeRoleFormValues>();

  const { roleId, teamId, clinicId } = watch();

  useEffect(() => {
    if (clinicId) {
      setFormValue('roleId', null);
    }
  }, [clinicId, setFormValue]);

  const clinicOptions = useMemo(
    () =>
      unionBy(
        current?.roles.map(option => ({ label: option.clinic?.name, value: option.clinic?.id })),
        'value',
      ),
    [current?.roles],
  );

  const roleOptions = useMemo(
    () =>
      unionBy(
        current?.roles.reduce((acc, obj) => {
          if (obj.clinic?.id === clinicId) {
            acc.push({ value: obj.id, label: removeActPrefix(obj?.name) });
          }

          return acc;
        }, []),
        'value',
      ),
    [clinicId, current?.roles],
  );

  const showClinicSelect = clinicOptions.length > 1;
  const showRoleSelect = !!clinicId || !showClinicSelect;

  const selectedRole = useMemo(
    () =>
      current?.roles.find(
        option => option.id === roleId && (clinicId ? clinicId === option.clinic?.id : true),
      ),
    [current?.roles, roleId, clinicId],
  );

  const memoizedSelectedRole = useMemoCompare(
    selectedRole,
    (prev, next) => prev && isEqual(prev, next),
  );

  const selectedTeam = useMemo(
    () => memoizedSelectedRole?.teams.find(option => option.id === teamId),
    [memoizedSelectedRole?.teams, teamId],
  );

  const teamOptions = useMemo(
    () => memoizedSelectedRole?.teams.map(option => ({ value: option.id, label: option?.name })),
    [memoizedSelectedRole],
  );

  const multipleRoles = current?.roles.length > 1;
  const multipleTeams = !multipleRoles && current?.roles[0]?.teams?.length > 1;

  const showTeamSelect =
    compareRoles(memoizedSelectedRole?.name, IUserRole.ActTeamMember) ||
    compareRoles(memoizedSelectedRole?.name, IUserRole.ActTeamLeader);

  useEffect(() => {
    if (!showTeamSelect) {
      setFormValue('teamId', null);
    }
  }, [showTeamSelect, setFormValue]);

  const isMultiTeamNewRole = compareRoles(memoizedSelectedRole?.name, IUserRole.ProgramAssistant);

  const canChangeTeam = useMemo(
    () => !!teamId && memoizedSelectedRole?.teams.some(item => item.id === teamId),
    [teamId, memoizedSelectedRole?.teams],
  );

  const canChangeClinic = useMemo(
    () => !!clinicId && memoizedSelectedRole?.clinic?.id === clinicId,
    [clinicId, memoizedSelectedRole?.clinic?.id],
  );

  const handleChangeRole = useCallback(async () => {
    const isSameRole = roleId === role?.id;
    const isSameTeam = teamId === team?.id;
    const isSameClinic = clinicId === clinic?.id;

    try {
      if (
        changingRole ||
        (showTeamSelect && !teamId) ||
        !roleId ||
        (!teamId && isSameRole && isSameClinic) ||
        (isSameTeam && isSameRole && isSameClinic) ||
        (showTeamSelect && !canChangeTeam) ||
        !canChangeClinic
      ) {
        return;
      }

      const defaultTeamId = showTeamSelect ? teamId : null;

      const payload = {
        roleId,
        teamId: isMultiTeamNewRole ? memoizedSelectedRole?.teams[0].id : defaultTeamId,
        clinicId,
      };

      if (isEqual(changeRolePayload, payload)) {
        return;
      }

      changeRolePayload = payload;

      setChangingRole(true);

      await onChangeRole(payload);

      const message = getChangeRoleSuccessMessage({
        teamFrom: team?.name,
        teamTo: selectedTeam?.name,
        roleFrom: removeActPrefix(role?.name),
        roleTo: removeActPrefix(memoizedSelectedRole?.name),
        clinicFrom: clinic?.name,
        clinicTo: memoizedSelectedRole?.clinic?.name,
      });

      setAvailableClients({ items: [], total: 0 });
      setClientOptionsByTeam({ items: [], total: 0 });
      setDefaultClient(null);
      setCurrentClientOption([]);
      if (clinic?.name !== memoizedSelectedRole?.clinic?.name) {
        setClinic(null);
        onGetMyClinic();
      }
      showNotify({ message });
    } catch (e) {
      showError(extractErrorMessage(e));
      setFormValue('teamId', null);
      setFormValue('roleId', null);
      setFormValue('clinicId', null);
    } finally {
      setChangingRole(false);
    }
  }, [
    roleId,
    role?.id,
    role?.name,
    teamId,
    team?.id,
    team?.name,
    clinicId,
    clinic?.id,
    clinic?.name,
    changingRole,
    showTeamSelect,
    canChangeTeam,
    canChangeClinic,
    isMultiTeamNewRole,
    memoizedSelectedRole?.teams,
    memoizedSelectedRole?.name,
    memoizedSelectedRole?.clinic?.name,
    onChangeRole,
    onGetMyClinic,
    selectedTeam?.name,
    setAvailableClients,
    setClientOptionsByTeam,
    setDefaultClient,
    setCurrentClientOption,
    setClinic,
    showNotify,
    showError,
    setFormValue,
  ]);

  useEffect(() => {
    if (roleId && roleId !== role?.id) {
      setFormValue('teamId', null);
    }
  }, [roleId, role?.id, setFormValue]);

  useEffect(() => {
    if (!teamId && team?.id) {
      setFormValue('teamId', team?.id);
    }
  }, [teamId, team?.id, setFormValue]);

  useEffect(() => {
    if (!roleId && role?.id) {
      setFormValue('roleId', role?.id);
    }
  }, [roleId, role?.id, setFormValue]);

  useEffect(() => {
    if (!roleId && !teamId && (role || team)) {
      setFormValue('roleId', role?.id);
      if (!isProgramAssistant) {
        setFormValue('teamId', team?.id);
      }
    }
  }, [isProgramAssistant, role, team, roleId, teamId, setFormValue]);

  useEffect(() => {
    if (!clinicId && clinic) {
      setFormValue('clinicId', clinic?.id);
    }
  }, [clinic, role, clinicId, setFormValue]);

  useEffect(() => {
    if (clinicOptions?.length === 1) {
      setFormValue('clinicId', clinicOptions[0].value);
    }
  }, [clinicOptions, setFormValue]);

  useEffect(() => {
    if (roleOptions?.length === 1) {
      setFormValue('roleId', roleOptions[0].value);
    }
  }, [roleOptions, setFormValue]);

  useEffect(() => {
    if (teamOptions?.length === 1) {
      setFormValue('teamId', teamOptions[0].value);
    }
  }, [teamOptions, setFormValue]);

  useEffect(() => {
    debounce(() => {
      handleChangeRole();
    }, DEBOUNCE_TIME_500)();
  }, [handleChangeRole]);

  return multipleRoles || multipleTeams ? (
    <Grid
      size={{
        sm: 5,
      }}
    >
      <div className={classNames(styles.itemsWrapper, styles.roleSelect)}>
        <h3 className={styles.header}>Role</h3>
        {showClinicSelect && (
          <div className={styles.row}>
            <Select
              name="clinicId"
              options={clinicOptions}
              control={control}
              errors={errors}
              label="Select Clinic"
              hideSelectedOptions={false}
            />
          </div>
        )}
        {showRoleSelect && (
          <div className={styles.row}>
            <Select
              name="roleId"
              options={roleOptions}
              control={control}
              errors={errors}
              label="Select Role"
              hideSelectedOptions={false}
            />
          </div>
        )}
        {!!roleId && showTeamSelect && (
          <div className={styles.row}>
            <Select
              name="teamId"
              options={teamOptions}
              control={control}
              errors={errors}
              label="Select Team"
              hideSelectedOptions={false}
            />
          </div>
        )}
      </div>
    </Grid>
  ) : null;
};

export default ChangeRole;
